// Вкладки раздела «Квоты» без карты: доли закрепления и ОДУ по видам квот.
//
// Доля — на весь срок договора и тонн не имеет (тонны года даёт распределение);
// перечень показывается как напечатан: вид и район словами приказа, без кода.
// ОДУ по видам квот — объём вида квоты (НИР, КМНС, любительская…) по виду и
// району, без пользователей: матрица «вид × район», столбцы — виды квот.

import { useEffect, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { cabinetApi } from '../api'
import { formatNumber } from '../i18n/format'
import { gapsLine } from './chainGaps'
import { useLang, useT } from '../i18n'

const focusRing = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-sonar'

export function SharesPanel({ basin }: { basin: string }) {
  const t = useT()
  const { lang } = useLang()
  const [open, setOpen] = useState<number | null>(null)
  const q = useQuery({ queryKey: ['quotas', 'shares', basin], queryFn: () => cabinetApi.quotaShares(basin) })
  const lists = q.data?.lists ?? []
  if (q.isError) return <p className="px-3 py-2 text-[12px] text-coral">{t('quotas.error')}</p>
  if (q.data && !lists.length) return <p className="px-3 py-2 text-[12px] text-mist">{t('quotas.sharesEmpty')}</p>
  const lst = open != null ? lists[open] : null
  if (lst) {
    return (
      <div className="px-3 py-2">
        <button type="button" onClick={() => setOpen(null)} className={`mb-2 text-[12px] text-mist hover:text-foam ${focusRing}`}>
          ← {t('quotas.allLists')}
        </button>
        <h2 className="text-[13px] font-semibold text-foam">{lst.vid}</h2>
        <p className="mb-2 text-[12px] text-mist">
          {lst.zone} · {t('quotas.listOf', { no: lst.list ?? '—', order: lst.order })}
        </p>
        <table className="w-full text-[12px]">
          <thead>
            <tr className="text-left text-mist">
              <th className="py-1 font-normal">{t('quotas.holder')}</th>
              <th className="py-1 text-right font-normal">{t('quotas.sharePct')}</th>
            </tr>
          </thead>
          <tbody>
            {lst.rows.map((r, i) => (
              <tr key={`${r.inn ?? r.name}-${i}`} className="border-t border-hull align-top">
                <td className="py-1 pr-2 text-foam">
                  {r.name}
                  <div className="text-[11px] text-mist">
                    {r.inn && <span className="whitespace-nowrap">{t('quotas.inn')} {r.inn}</span>}
                    {r.object && <span className="ml-2">{r.object}</span>}
                  </div>
                </td>
                <td className="tnum whitespace-nowrap py-1 text-right text-foam">{formatNumber(lang, r.share, 3)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }
  return (
    <>
    {(q.data?.gaps ?? []).map(g => (
      <p key={`${g.order}-${g.date}`} className="border-b border-hull px-3 py-2 text-[11px] text-buoy">
        {t('quotas.incomplete', { no: g.order, missing: gapsLine(g.gaps, lang, t) })}
      </p>
    ))}
    <ul>
      {lists.map((l, i) => (
        <li key={`${l.order}-${l.list}-${l.vid}`}>
          <button type="button" onClick={() => setOpen(i)}
            className={`flex w-full items-baseline gap-2 border-b border-hull px-3 py-2 text-left hover:bg-deep ${focusRing}`}>
            <span className="min-w-0 flex-1 text-[12px] text-foam">
              {l.vid}
              <span className="block text-[11px] text-mist">
                {l.zone} · {t('quotas.holdersCount', { count: l.holders })}
              </span>
            </span>
            <span className="tnum shrink-0 whitespace-nowrap text-[12px] text-mist">{formatNumber(lang, l.total, 1)} %</span>
          </button>
        </li>
      ))}
    </ul>
    </>
  )
}

export function TacPanel({ basin, year, onYears }: {
  basin: string
  year: number | null
  onYears: (years: number[], year: number | null) => void
}) {
  const t = useT()
  const { lang } = useLang()
  const q = useQuery({
    queryKey: ['quotas', 'tac', basin, year],
    queryFn: () => cabinetApi.quotaTac({ basin, year }),
    placeholderData: keepPreviousData,
  })
  const d = q.data
  // Годы у ОДУ свои: переключатель года в шапке раздела берёт их отсюда.
  useEffect(() => {
    if (d) onYears(d.years, d.year)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d])
  if (q.isError) return <p className="px-3 py-2 text-[12px] text-coral">{t('quotas.error')}</p>
  if (d && !d.rows.length) return <p className="px-3 py-2 text-[12px] text-mist">{t('quotas.tacEmpty')}</p>
  const tons = (v: number | undefined) => (v == null ? '—' : formatNumber(lang, v, v < 10 ? 3 : 1))
  return (
    <div className="overflow-x-auto px-3 py-2">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="text-left align-bottom text-mist">
            <th className="py-1 pr-2 font-normal">{t('quotas.species')}</th>
            {(d?.kinds ?? []).map(k => (
              <th key={k} className="py-1 pl-2 text-right font-normal">{t(`quotas.tacKind.${k}`)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(d?.rows ?? []).map(r => (
            <tr key={`${r.vid}|${r.parent}|${r.zone}`} className="border-t border-hull align-top">
              <td className="py-1 pr-2 text-foam">
                {r.vid}
                <div className="text-[11px] text-mist">{r.parent ? `${r.parent} → ${r.zone}` : r.zone}</div>
              </td>
              {(d?.kinds ?? []).map(k => (
                <td key={k} className="tnum whitespace-nowrap py-1 pl-2 text-right text-foam">{tons(r.tons[k])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
